import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { playbookStory, pickLang } from '../../data/playbookStory';
import { businessLogic } from '../../lib/constants';
import { trackConversion } from '../../lib/analytics';
import PlaybookStage, { StageIcon } from './PlaybookStage';
import DemoModal from './DemoModal';

/**
 * Recorrido de un Playbook contado con el scroll.
 *
 * Cómo avanza (y por qué así):
 *  - La escena se queda `sticky` mientras el envoltorio, mucho más alto, sigue
 *    pasando. NO se secuestra el scroll: es scroll nativo, la barra sigue ahí y
 *    el usuario puede irse cuando quiera.
 *  - La etapa activa NO se calcula con un listener de scroll. Hay un centinela
 *    invisible por etapa y un IntersectionObserver con rootMargin -50%/-50%,
 *    que dispara justo cuando cada centinela cruza el CENTRO de la pantalla.
 *    Sale gratis, y funciona igual hacia delante que hacia atrás.
 *  - Debajo de 992px y con prefers-reduced-motion NO hay sticky: las etapas se
 *    apilan y se ven todas. Un sticky alto en móvil es incómodo y el contenido
 *    tiene que entenderse sin animación.
 *
 * Las etapas ya recorridas se quedan marcadas en el indicador, para no perder
 * el contexto de por dónde va la historia.
 */
const STICKY_MIN_WIDTH = 992;

const PlaybookScrollStory = () => {
   const { t, i18n } = useTranslation();
   const lang = i18n?.language === 'en' ? 'en' : 'es';
   const stages = playbookStory.stages;

   const [active, setActive] = useState(0);
   const [sticky, setSticky] = useState(false);
   const [demo, setDemo] = useState(null);
   const sentinelRefs = useRef([]);

   // ¿Toca modo sticky? Depende del ancho y de si el usuario pidió menos
   // movimiento. Se recalcula al redimensionar (y al girar el móvil).
   useEffect(() => {
      const decide = () => {
         let reduced = false;
         try {
            reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
         } catch (e) {
            reduced = false;
         }
         setSticky(window.innerWidth >= STICKY_MIN_WIDTH && !reduced);
      };
      decide();
      window.addEventListener('resize', decide);
      return () => window.removeEventListener('resize', decide);
   }, []);

   // Centinelas → etapa activa. Solo en modo sticky: apilado no hay "activa".
   useEffect(() => {
      if (!sticky || typeof IntersectionObserver === 'undefined') return undefined;
      const observer = new IntersectionObserver(
         (entries) => {
            entries.forEach((entry) => {
               if (!entry.isIntersecting) return;
               const idx = Number(entry.target.dataset.stageIndex);
               if (!Number.isNaN(idx)) setActive(idx);
            });
         },
         // La franja de 0px de alto en el centro exacto del viewport.
         { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
      );
      sentinelRefs.current.forEach((node) => node && observer.observe(node));
      return () => observer.disconnect();
   }, [sticky]);

   const openDemo = useCallback((stageDemo) => {
      if (!stageDemo) return;
      const url =
         stageDemo.key === 'sca'
            ? businessLogic.scaEmbedUrl
            : businessLogic.discoveryEmbedUrl;
      if (!url) return;
      trackConversion(`Home Story - Demo ${stageDemo.key}`);
      setDemo({ url, title: stageDemo.title });
   }, []);

   const closeDemo = useCallback(() => setDemo(null), []);

   // Pulsar una etapa DESPLAZA hasta ella; no toca `active` a mano. La etapa
   // activa la manda la posicion de scroll (los centinelas), asi que forzar el
   // estado se desharia solo en cuanto el usuario moviese la rueda un pixel.
   // Al llevar el centinela al centro de la pantalla, el observer hace el resto.
   const goToStage = useCallback((index) => {
      const node = sentinelRefs.current[index];
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const target = rect.top + window.scrollY + rect.height / 2 - window.innerHeight / 2;
      let smooth = true;
      try {
         smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (e) {
         smooth = true;
      }
      window.scrollTo({ top: target, behavior: smooth ? 'smooth' : 'auto' });
   }, []);

   const stageOf = (stage, index) => (
      <PlaybookStage
         stage={stage}
         index={index}
         total={stages.length}
         lang={lang}
         estimateNotice={playbookStory.estimateNotice}
         onOpenDemo={openDemo}
      />
   );

   return (
      <section className="pbs">
         <div className="container">
            <div className="row">
               <div className="col-xl-8 offset-xl-2 col-lg-10 offset-lg-1 text-center">
                  <span className="pbs__kicker">{t('pbs_kicker')}</span>
                  <h2 className="pbs__title">{t('pbs_title')}</h2>
                  <p className="pbs__subtitle">{t('pbs_subtitle')}</p>
               </div>
            </div>
         </div>

         {sticky ? (
            // ---------- Escritorio: un mismo escenario que se transforma ----------
            <div className="pbs__scroller">
               <div className="pbs__sentinels" aria-hidden="true">
                  {stages.map((stage, index) => (
                     <div
                        key={stage.key}
                        className="pbs__sentinel"
                        data-stage-index={index}
                        ref={(node) => {
                           sentinelRefs.current[index] = node;
                        }}
                     />
                  ))}
               </div>

               <div className="pbs__sticky">
                  <div className="container">
                     <ol className="pbs__progress">
                        {stages.map((stage, index) => (
                           <li key={stage.key}>
                              <button
                                 type="button"
                                 className={`pbs__progress-item${
                                    index === active ? ' is-active' : ''
                                 }${index < active ? ' is-done' : ''}`}
                                 onClick={() => goToStage(index)}
                                 aria-current={index === active ? 'step' : undefined}
                              >
                                 <StageIcon name={stage.icon} size={16} />
                                 <span>{pickLang(stage.name, lang)}</span>
                              </button>
                           </li>
                        ))}
                     </ol>

                     <div className="pbs__scene">
                        {stages.map((stage, index) => (
                           <div
                              key={stage.key}
                              className={`pbs__slot${index === active ? ' is-active' : ''}`}
                              // Lo que no es la etapa activa no debe ser
                              // navegable con el tabulador ni existir para un
                              // lector de pantalla: está apilado detrás.
                              aria-hidden={index !== active}
                              inert={index !== active ? '' : undefined}
                           >
                              {stageOf(stage, index)}
                           </div>
                        ))}
                     </div>
                  </div>
               </div>
            </div>
         ) : (
            // ---------- Móvil / reduced-motion: etapas apiladas ----------
            <div className="container">
               <div className="pbs__stacked">
                  {stages.map((stage, index) => (
                     <div className="pbs__stacked-item" key={stage.key}>
                        {stageOf(stage, index)}
                     </div>
                  ))}
               </div>
            </div>
         )}

         <DemoModal
            open={Boolean(demo)}
            url={demo ? demo.url : null}
            title={demo ? pickLang(demo.title, lang) : ''}
            onClose={closeDemo}
         />
      </section>
   );
};

export default PlaybookScrollStory;
